import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';

import { markManualIdCheck, skipManualIdCheck, useMutateApiData } from '../../util/api';
import { ErrorMessage, SimpleCard } from '../common';
import { SectionField } from '../common/section';

interface ManualIdCheckProps {
  reservationId: string;
  manualIdChecked: boolean;
  manualIdCheckEnabled: boolean;
  manualIdCheckSkipped?: boolean;
  refetchReservation: () => Promise<unknown>;
}

export const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  });
});

const ManualIdCheck: React.FC<ManualIdCheckProps> = ({
  reservationId,
  manualIdCheckEnabled = false,
  manualIdChecked = false,
  manualIdCheckSkipped = false,
  refetchReservation,
}) => {
  if (!manualIdCheckEnabled) {
    return null;
  }

  const classes = useStyles();
  const { t } = useTranslation();

  const { error, isLoading, mutateAsync: markManualIdCheckRequest } = useMutateApiData(markManualIdCheck);
  const { error: skipError, isLoading: isSkipActionLoading, mutateAsync: skipManualIdCheckRequest } = useMutateApiData(skipManualIdCheck);

  const markAsChecked = async (): Promise<void> => {
    await markManualIdCheckRequest([reservationId]);
    await refetchReservation();
  };

  const skipIdCheck = async (): Promise<void> => {
    await skipManualIdCheckRequest([reservationId]);
    await refetchReservation();
  };

  const showActionButtons = !manualIdChecked;
  const idCheckedText = manualIdChecked ? "✅ yes" : '❌ no';
  return (
    <SimpleCard title={t('sections.manualIdCheck')}>
      <SectionField label='ID Checked'>{idCheckedText}</SectionField>
      {showActionButtons && (
        <Box flexDirection="row">
          <Button
            variant="outlined"
            color="primary"
            onClick={markAsChecked}
            disabled={isLoading}
            className={classes.button}
          >
            {t('general.markAsChecked')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={skipIdCheck}
            disabled={isSkipActionLoading || manualIdCheckSkipped}
            className={classes.button}
          >
            {t('general.skipIdCheck')}
          </Button>

          {isLoading && <Typography>{t('general.loading')}</Typography>}
        </Box>
      )}

      {(error ?? skipError) && <ErrorMessage message={t('general.error')} />}
    </SimpleCard>
  );
};

export default ManualIdCheck;
